package com.milta.core.phone

/**
 * Détection du pays via l'indicatif et normalisation des numéros de téléphone en E.164.
 *
 * Règle par défaut : un numéro saisi SANS préfixe international (`+` ou `00`)
 * est considéré comme belge (+32). Le drapeau affiché suit l'indicatif tapé,
 * pour TOUS les pays (table indicatif→ISO + drapeau généré depuis le code ISO).
 */
object PhoneNumber {

    const val DEFAULT_DIAL_CODE = "32"
    const val DEFAULT_ISO = "BE"

    /** Drapeau affiché quand un indicatif international n'est pas reconnu. */
    const val UNKNOWN_FLAG = "🌐"

    /**
     * Indicatifs téléphoniques internationaux → code pays ISO 3166-1 alpha-2.
     * Le match retient le plus long indicatif (ex. "359" Bulgarie, "1242" Bahamas).
     * Zone 1 (NANP) : "1" → US par défaut, les territoires des Caraïbes ont leur préfixe complet.
     */
    internal val dialCodeToIso: List<Pair<String, String>> = listOf(
        // ── Zone 1 — NANP ──
        "1" to "US",
        "1242" to "BS", "1246" to "BB", "1264" to "AI", "1268" to "AG", "1284" to "VG",
        "1340" to "VI", "1345" to "KY", "1441" to "BM", "1473" to "GD", "1649" to "TC",
        "1658" to "JM", "1664" to "MS", "1670" to "MP", "1671" to "GU", "1684" to "AS",
        "1721" to "SX", "1758" to "LC", "1767" to "DM", "1784" to "VC", "1787" to "PR",
        "1809" to "DO", "1829" to "DO", "1849" to "DO", "1868" to "TT", "1869" to "KN",
        "1876" to "JM", "1939" to "PR",
        // ── Zone 2 — Afrique ──
        "20" to "EG", "211" to "SS", "212" to "MA", "213" to "DZ", "216" to "TN",
        "218" to "LY", "220" to "GM", "221" to "SN", "222" to "MR", "223" to "ML",
        "224" to "GN", "225" to "CI", "226" to "BF", "227" to "NE", "228" to "TG",
        "229" to "BJ", "230" to "MU", "231" to "LR", "232" to "SL", "233" to "GH",
        "234" to "NG", "235" to "TD", "236" to "CF", "237" to "CM", "238" to "CV",
        "239" to "ST", "240" to "GQ", "241" to "GA", "242" to "CG", "243" to "CD",
        "244" to "AO", "245" to "GW", "246" to "IO", "248" to "SC", "249" to "SD",
        "250" to "RW", "251" to "ET", "252" to "SO", "253" to "DJ", "254" to "KE",
        "255" to "TZ", "256" to "UG", "257" to "BI", "258" to "MZ", "260" to "ZM",
        "261" to "MG", "262" to "RE", "263" to "ZW", "264" to "NA", "265" to "MW",
        "266" to "LS", "267" to "BW", "268" to "SZ", "269" to "KM", "27" to "ZA",
        "290" to "SH", "291" to "ER", "297" to "AW", "298" to "FO", "299" to "GL",
        // ── Zones 3 & 4 — Europe ──
        "30" to "GR", "31" to "NL", "32" to "BE", "33" to "FR", "34" to "ES",
        "350" to "GI", "351" to "PT", "352" to "LU", "353" to "IE", "354" to "IS",
        "355" to "AL", "356" to "MT", "357" to "CY", "358" to "FI", "359" to "BG",
        "36" to "HU", "370" to "LT", "371" to "LV", "372" to "EE", "373" to "MD",
        "374" to "AM", "375" to "BY", "376" to "AD", "377" to "MC", "378" to "SM",
        "379" to "VA", "380" to "UA", "381" to "RS", "382" to "ME", "383" to "XK",
        "385" to "HR", "386" to "SI", "387" to "BA", "389" to "MK", "39" to "IT",
        "40" to "RO", "41" to "CH", "420" to "CZ", "421" to "SK", "423" to "LI",
        "43" to "AT", "44" to "GB", "45" to "DK", "46" to "SE", "47" to "NO",
        "48" to "PL", "49" to "DE",
        // ── Zone 5 — Amériques ──
        "500" to "FK", "501" to "BZ", "502" to "GT", "503" to "SV", "504" to "HN",
        "505" to "NI", "506" to "CR", "507" to "PA", "508" to "PM", "509" to "HT",
        "51" to "PE", "52" to "MX", "53" to "CU", "54" to "AR", "55" to "BR",
        "56" to "CL", "57" to "CO", "58" to "VE", "590" to "GP", "591" to "BO",
        "592" to "GY", "593" to "EC", "594" to "GF", "595" to "PY", "596" to "MQ",
        "597" to "SR", "598" to "UY", "599" to "CW",
        // ── Zone 6 — Océanie / Asie du Sud-Est ──
        "60" to "MY", "61" to "AU", "62" to "ID", "63" to "PH", "64" to "NZ",
        "65" to "SG", "66" to "TH", "670" to "TL", "672" to "NF", "673" to "BN",
        "674" to "NR", "675" to "PG", "676" to "TO", "677" to "SB", "678" to "VU",
        "679" to "FJ", "680" to "PW", "681" to "WF", "682" to "CK", "683" to "NU",
        "685" to "WS", "686" to "KI", "687" to "NC", "688" to "TV", "689" to "PF",
        "690" to "TK", "691" to "FM", "692" to "MH",
        // ── Zone 7 — Russie / Kazakhstan ──
        "7" to "RU", "76" to "KZ", "77" to "KZ",
        // ── Zone 8 — Asie de l'Est ──
        "81" to "JP", "82" to "KR", "84" to "VN", "850" to "KP", "852" to "HK",
        "853" to "MO", "855" to "KH", "856" to "LA", "86" to "CN", "880" to "BD",
        "886" to "TW",
        // ── Zone 9 — Asie de l'Ouest / du Sud ──
        "90" to "TR", "91" to "IN", "92" to "PK", "93" to "AF", "94" to "LK",
        "95" to "MM", "960" to "MV", "961" to "LB", "962" to "JO", "963" to "SY",
        "964" to "IQ", "965" to "KW", "966" to "SA", "967" to "YE", "968" to "OM",
        "970" to "PS", "971" to "AE", "972" to "IL", "973" to "BH", "974" to "QA",
        "975" to "BT", "976" to "MN", "977" to "NP", "98" to "IR", "992" to "TJ",
        "993" to "TM", "994" to "AZ", "995" to "GE", "996" to "KG", "998" to "UZ",
    )

    /** Convertit un code ISO 3166-1 alpha-2 en emoji drapeau (paire de symboles indicateurs régionaux). */
    internal fun flagFromIso(iso: String): String = buildString {
        for (c in iso.uppercase()) {
            val codePoint = 0x1F1E6 + (c.code - 'A'.code) // indicateur régional
            val offset = codePoint - 0x10000
            append((0xD800 + (offset shr 10)).toChar())   // high surrogate
            append((0xDC00 + (offset and 0x3FF)).toChar()) // low surrogate
        }
    }

    /** Indicatif international en chiffres (sans `+`), ou null si l'entrée n'a pas de préfixe international. */
    private fun internationalDigits(raw: String): String? {
        val trimmed = raw.trim()
        return when {
            trimmed.startsWith("+") -> trimmed.drop(1).filter { it.isDigit() }
            trimmed.startsWith("00") -> trimmed.drop(2).filter { it.isDigit() }
            else -> null
        }
    }

    /**
     * Drapeau correspondant au numéro saisi :
     * - sans préfixe international → Belgique (🇧🇪)
     * - indicatif reconnu → drapeau du pays (le plus long indicatif gagne)
     * - indicatif international non reconnu → drapeau neutre (🌐)
     */
    fun flagFor(raw: String): String {
        val intl = internationalDigits(raw) ?: return flagFromIso(DEFAULT_ISO)
        if (intl.isEmpty()) return flagFromIso(DEFAULT_ISO)
        val iso = dialCodeToIso
            .filter { (code, _) -> intl.startsWith(code) }
            .maxByOrNull { (code, _) -> code.length }
            ?.second
        return if (iso != null) flagFromIso(iso) else UNKNOWN_FLAG
    }

    /**
     * Normalise vers E.164 (`+<indicatif><numéro>`) :
     * - `+…`  → conservé (seuls les chiffres sont gardés après le `+`)
     * - `00…` → converti en `+…`
     * - sans préfixe → belge : `+32` + numéro (un éventuel `0` initial est retiré)
     *
     * Idempotent : normalize(normalize(x)) == normalize(x).
     */
    fun normalize(raw: String): String {
        val cleaned = raw.trim().filter { it.isDigit() || it == '+' }
        if (cleaned.isBlank() || cleaned == "+") return cleaned
        return when {
            cleaned.startsWith("+") -> "+" + cleaned.drop(1).filter { it.isDigit() }
            cleaned.startsWith("00") -> "+" + cleaned.drop(2).filter { it.isDigit() }
            else -> "+$DEFAULT_DIAL_CODE" + cleaned.filter { it.isDigit() }.trimStart('0')
        }
    }
}
